(() => {
  'use strict';

  const focusReturn = new WeakMap();
  const liveRegion = () => document.getElementById('LiveRegion');

  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  window.PurestWipesTheme = { announce };

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
        if (!button) return;
        const input = this.querySelector('input[type="number"]');
        if (!input) return;
        const previous = input.value;
        button.hasAttribute('data-quantity-plus') ? input.stepUp() : input.stepDown();
        if (input.value !== previous) input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.items = [...this.querySelectorAll('[data-announcement-item]')];
      this.index = Math.max(0, this.items.findIndex((item) => !item.hidden));
      this.querySelector('[data-announcement-previous]')?.addEventListener('click', () => this.show(this.index - 1));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1));
      if (this.items.length > 1 && this.dataset.autoRotate === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const interval = Math.max(4000, Number(this.dataset.interval) || 6000);
        this.timer = window.setInterval(() => this.show(this.index + 1), interval);
        const stopAutoRotate = () => {
          window.clearInterval(this.timer);
          this.timer = null;
        };
        this.addEventListener('mouseenter', stopAutoRotate, { once: true });
        this.addEventListener('focusin', stopAutoRotate, { once: true });
      }
    }

    disconnectedCallback() { window.clearInterval(this.timer); }

    show(nextIndex) {
      if (!this.items.length) return;
      this.index = (nextIndex + this.items.length) % this.items.length;
      this.items.forEach((item, index) => {
        item.hidden = index !== this.index;
        item.classList.toggle('is-active', index === this.index);
      });
      const counter = this.querySelector('[data-announcement-current]');
      if (counter) counter.textContent = String(this.index + 1);
    }
  }

  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-dialog-open]');
    if (openButton) {
      const dialog = document.querySelector(openButton.dataset.dialogOpen);
      if (dialog instanceof HTMLDialogElement) {
        focusReturn.set(dialog, openButton);
        dialog.addEventListener('close', () => {
          const returnTarget = focusReturn.get(dialog);
          if (returnTarget?.isConnected) returnTarget.focus();
        }, { once: true });
        dialog.showModal();
        window.requestAnimationFrame(() => {
          const input = dialog.querySelector('[data-predictive-search-input]');
          (input || dialog.querySelector('input, button, a'))?.focus();
        });
      }
      return;
    }

    const closeButton = event.target.closest('[data-dialog-close]');
    if (closeButton) {
      const dialog = closeButton.closest('dialog');
      dialog?.close();
      return;
    }

    const drawerLink = event.target.closest('details[data-drawer] a[href]');
    if (drawerLink) {
      const details = drawerLink.closest('details[data-drawer]');
      if (details) {
        const url = new URL(drawerLink.href, window.location.href);
        const isSamePage = url.origin === window.location.origin
          && url.pathname === window.location.pathname
          && url.search === window.location.search;
        details.open = false;
        if (isSamePage && url.hash) {
          window.requestAnimationFrame(() => {
            const target = document.querySelector(url.hash);
            if (!target) return;
            const hadTabindex = target.hasAttribute('tabindex');
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            if (!hadTabindex) {
              target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
            }
          });
        }
      }
      return;
    }

    const drawerClose = event.target.closest('[data-drawer-close]');
    if (drawerClose) {
      const details = drawerClose.closest('details[data-drawer]');
      if (details) {
        details.open = false;
        details.querySelector('summary')?.focus();
      }
      return;
    }

    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
    const openDrawer = document.querySelector('details[data-drawer][open]');
    if (openDrawer && event.target === openDrawer) {
      openDrawer.open = false;
      openDrawer.querySelector('summary')?.focus();
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof HTMLDialogElement) event.target.close();
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-select]');
    if (select) select.form?.submit();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector('details[data-drawer][open]');
    if (drawer) {
      drawer.open = false;
      drawer.querySelector('summary')?.focus();
      return;
    }
    const disclosure = document.querySelector('details[data-disclosure][open]');
    if (disclosure) {
      disclosure.open = false;
      disclosure.querySelector('summary')?.focus();
    }
  });

  const initializeDrawers = (scope = document) => {
    scope.querySelectorAll('details[data-drawer]').forEach((details) => {
      if (details.dataset.focusTrap === 'true') return;
      details.dataset.focusTrap = 'true';
      const trigger = details.querySelector(':scope > summary');
      const panel = details.querySelector('[role="dialog"]');
      details.addEventListener('toggle', () => {
        if (details.open) {
          window.requestAnimationFrame(() => panel?.querySelector('a, button, summary, input')?.focus());
          return;
        }
        if (panel?.contains(document.activeElement)) {
          const fallback = trigger?.getClientRects().length ? trigger : document.querySelector('.site-header__logo-link');
          fallback?.focus();
        }
      });
      panel?.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((element) => element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  };
  initializeDrawers();

  const mobileDrawerQuery = window.matchMedia('(max-width: 900px)');
  const supplyCardOrder = new WeakMap();
  const updateSupplyCardOrder = (grid) => {
    const originalOrder = supplyCardOrder.get(grid);
    if (!originalOrder) return;
    const featured = originalOrder.find((card) => card.classList.contains('pw-supply-card--featured'));
    const desiredOrder = mobileDrawerQuery.matches && featured
      ? [featured, ...originalOrder.filter((card) => card !== featured)]
      : originalOrder;
    const currentOrder = [...grid.children];
    if (desiredOrder.every((card, index) => currentOrder[index] === card)) return;
    const active = grid.contains(document.activeElement) ? document.activeElement : null;
    desiredOrder.forEach((card) => grid.append(card));
    if (active?.isConnected) active.focus({ preventScroll: true });
  };
  const initializeSupplyCardOrder = (scope = document) => {
    scope.querySelectorAll('.pw-supply__cards').forEach((grid) => {
      if (!supplyCardOrder.has(grid)) supplyCardOrder.set(grid, [...grid.children]);
      updateSupplyCardOrder(grid);
    });
  };
  initializeSupplyCardOrder();
  mobileDrawerQuery.addEventListener('change', (event) => {
    document.querySelectorAll('.pw-supply__cards').forEach(updateSupplyCardOrder);
    if (!event.matches) {
      document.querySelectorAll('details[data-drawer][open]').forEach((details) => {
        const focusWasInside = details.contains(document.activeElement);
        details.open = false;
        if (focusWasInside) {
          window.requestAnimationFrame(() => document.querySelector('.site-header__logo-link')?.focus());
        }
      });
    }
  });

  const updateHeaderHeight = () => {
    const header = document.querySelector('[data-header]');
    if (header) document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };
  updateHeaderHeight();
  window.addEventListener('resize', updateHeaderHeight, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    updateHeaderHeight();
    initializeDrawers(event.target);
    initializeSupplyCardOrder(event.target);
  });
})();
